import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './ProductListingPage.css';
import productsData from '../data/products.json';
import { optimizeImage } from '../utils/imageOptimizer';

const ITEMS_PER_PAGE = 12;

const ProductListingPage = () => {
    const location = useLocation();
    const [activeFilter, setActiveFilter] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);

    // Read search query from URL if navigated from navbar search
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('search') || '';

    // When URL search param changes (navbar search), reset filter + page
    useEffect(() => {
        if (searchQuery) setActiveFilter('All');
        setCurrentPage(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Reset page when filter tab changes manually
    useEffect(() => {
        setCurrentPage(1);
    }, [activeFilter]);

    // Filter products
    const filteredProducts = productsData.filter(prod => {
        const matchesFilter =
            activeFilter === 'All' ||
            (activeFilter === 'Dogs' && prod.type === 'dog') ||
            (activeFilter === 'Cats' && prod.type === 'cat');
        const matchesSearch = searchQuery
            ? prod.name.toLowerCase().includes(searchQuery.toLowerCase())
            : true;
        return matchesFilter && matchesSearch;
    });

    const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentProducts = filteredProducts.slice(startIdx, startIdx + ITEMS_PER_PAGE);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo({ top: 200, behavior: 'smooth' });
        }
    };

    const filters = ['All', 'Dogs', 'Cats'];

    // Build page number array (show max 5 around current)
    const getPageNumbers = () => {
        const pages = [];
        const delta = 2;
        const left = Math.max(1, currentPage - delta);
        const right = Math.min(totalPages, currentPage + delta);
        for (let i = left; i <= right; i++) pages.push(i);
        return pages;
    };

    return (
        <div className="plp-wrapper">
            <div className="plp-inner">
            {/* Page Title */}
            <div className="plp-header">
                <h1 className="plp-title">Explore Our Nutrition Range</h1>
                {searchQuery && (
                    <p className="plp-search-info">
                        Showing results for: <strong>"{searchQuery}"</strong>
                        <Link to="/products" className="plp-clear-search">Clear</Link>
                    </p>
                )}
            </div>

            {/* Filter Tabs */}
            <div className="plp-filters">
                {filters.map((f, idx) => (
                    <React.Fragment key={f}>
                        <button
                            className={`plp-filter-btn ${activeFilter === f ? 'active' : ''}`}
                            onClick={() => setActiveFilter(f)}
                        >
                            {f === 'All' ? 'ALL' : f === 'Dogs' ? 'DOG FOODS' : 'CAT FOODS'}
                        </button>
                        {idx < filters.length - 1 && <span className="filter-sep">/</span>}
                    </React.Fragment>
                ))}
            </div>

            {/* Product Grid */}
            {currentProducts.length > 0 ? (
                <>
                    <div className="plp-grid">
                        {currentProducts.map(prod => (
                            <Link
                                to={`/${prod.type}-products/${prod.id}`}
                                key={prod.id}
                                className="plp-card"
                            >
                                <div className="plp-card-img-wrap">
                                    <img 
                                        src={optimizeImage(prod.img, { width: 300, height: 300 })} 
                                        alt={prod.name} 
                                        className="plp-card-img" 
                                        loading="lazy"
                                        width="300"
                                        height="300"
                                    />
                                    {prod.isComingSoon && (
                                        <div className="coming-soon-label">Coming Soon</div>
                                    )}
                                    <div className="plp-enquire-btn">
                                        <span>View Details</span>
                                    </div>
                                </div>
                                <div className="plp-card-info">
                                    <h3 className="plp-card-name">{prod.name}</h3>
                                    {!prod.isComingSoon && <p className="plp-card-price">{prod.price}</p>}
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="plp-pagination">
                            <button
                                className="plp-page-btn arrow"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                aria-label="Previous page"
                            >
                                &#8592;
                            </button>

                            {currentPage > 3 && (
                                <>
                                    <button className="plp-page-btn" onClick={() => handlePageChange(1)}>1</button>
                                    {currentPage > 4 && <span className="plp-ellipsis">…</span>}
                                </>
                            )}

                            {getPageNumbers().map(num => (
                                <button
                                    key={num}
                                    className={`plp-page-btn ${num === currentPage ? 'current' : ''}`}
                                    onClick={() => handlePageChange(num)}
                                >
                                    {num}
                                </button>
                            ))}

                            {currentPage < totalPages - 2 && (
                                <>
                                    {currentPage < totalPages - 3 && <span className="plp-ellipsis">…</span>}
                                    <button className="plp-page-btn" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
                                </>
                            )}

                            <button
                                className="plp-page-btn arrow"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                aria-label="Next page"
                            >
                                &#8594;
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div className="plp-empty">
                    <p>No products found.</p>
                    <button onClick={() => setActiveFilter('All')} className="plp-reset-btn">
                        View All Products
                    </button>
                </div>
            )}
            </div>
        </div>
    );
};

export default ProductListingPage;
